import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useState } from "react";
import { Form, Offcanvas } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import Swal from "sweetalert2";
import { Client } from "../../models/client.model";
import { citiesService } from "../../service/cities.service";
import { clientService } from "../../service/clientService";
import { countryService } from "../../service/country.service";
import { statesService } from "../../service/state.service";
import PageHeader from "../PageHeader";

const initialClient: Partial<Client> = {
  name: "",
  code: "",
  description: "",
  address: "",
  cityId: 0,
  zipCode: "",
  countryId: 0,
  stateId: 0,
};

const ClientMasterComponent: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [form, setForm] = useState<Partial<Client>>(initialClient);
  const [isEditing, setIsEditing] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [listLoading, setListLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState<1 | -1>(1);
  const [page, setPage] = useState(() => {
    const savedPage = localStorage.getItem("clientMasterPage");
    return savedPage ? parseInt(savedPage) : 1;
  });
  const [rows, setRows] = useState(() => {
    const savedRows = localStorage.getItem("clientMasterRows");
    return savedRows ? parseInt(savedRows) : 10;
  });
  const [totalRecords, setTotalRecords] = useState(0);
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);

  // Fetch clients for table
  const fetchClients = async () => {
    setListLoading(true);
    try {
      const result = await clientService.getAllPaginated({
        pageNumber: page,
        pageSize: rows,
        sortField: sortField,
        sortOrder: sortOrder === 1 ? "asc" : "desc",
        globalFilter,
      });
      setClients(
        (result?.items ?? []).map((item: any) => ({
          ...item,
          id: typeof item.id === "string" ? parseInt(item.id, 10) : item.id,
        }))
      );
      setTotalRecords(result?.totalCount ?? 0);
    } catch {
      setClients([]);
      setTotalRecords(0);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [page, rows, globalFilter, sortField, sortOrder]);

  useEffect(() => {
    countryService.getAll().then((data: any[]) => setCountries(data));
  }, []);

  useEffect(() => {
    if (form.countryId) {
      statesService
        .getByCountryId(form.countryId)
        .then((data: any[]) => setStates(data));
      setForm((prev) => ({ ...prev, stateId: 0, cityId: 0 }));
      setCities([]);
    }
  }, [form.countryId]);

  useEffect(() => {
    if (form.stateId) {
      citiesService
        .getByStateId(form.stateId)
        .then((data: any[]) => setCities(data));
      setForm((prev) => ({ ...prev, cityId: 0 }));
    }
  }, [form.stateId]);

  // Validation logic
  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!form.name?.trim()) newErrors.name = "Name is required";
    else if (!/^[a-zA-Z\s]+$/.test(form.name.trim()))
      newErrors.name = "Name must contain only alphabets";
    if (!form.cityId || form.cityId === 0)
      newErrors.cityId = "City is required";
    if (!form.zipCode?.trim()) newErrors.zipCode = "Zip Code is required";
    else if (!/^\d{1,6}$/.test(form.zipCode.trim()))
      newErrors.zipCode = "Zip Code must be up to 6 digits";
    if (!form.description?.trim())
      newErrors.description = "Description is required";
    if (!form.address?.trim()) newErrors.address = "Address is required";
    if (!form.countryId || form.countryId === 0)
      newErrors.countryId = "Country is required";
    if (!form.stateId || form.stateId === 0)
      newErrors.stateId = "State is required";
    return newErrors;
  };

  // Form change handler
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    if (name === "name") {
      const val = value.replace(/[^a-zA-Z\s]/g, "");
      setForm({ ...form, [name]: val });
      setErrors({ ...errors, [name]: "" });
    } else if (name === "zipCode") {
      const val = value.replace(/\D/g, "").slice(0, 6);
      setForm({ ...form, [name]: val });
      setErrors({ ...errors, [name]: "" });
    } else if (name === "countryId") {
      setForm({ ...form, countryId: Number(value), stateId: 0, cityId: 0 });
      setErrors({ ...errors, countryId: "" });
    } else if (name === "stateId") {
      setForm({ ...form, stateId: Number(value), cityId: 0 });
      setErrors({ ...errors, stateId: "" });
    } else if (name === "cityId") {
      setForm({ ...form, cityId: Number(value) });
      setErrors({ ...errors, cityId: "" });
    } else {
      setForm({ ...form, [name]: value });
      setErrors({ ...errors, [name]: "" });
    }
  };

  // Create or update client
  const handleAddOrUpdate = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    try {
      if (isEditing && editingIndex !== null) {
        await clientService.update(clients[editingIndex].id!, form as Client);
        const updatedClients = [...clients];
        updatedClients[editingIndex] = { ...clients[editingIndex], ...form };
        setClients(updatedClients);
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Client updated successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      } else {
        // TODO: Replace '1' with the actual rmcId value as needed
        await clientService.createClient(form as Client);
        await fetchClients();
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Client created successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
      setForm(initialClient);
      setIsEditing(false);
      setEditingIndex(null);
      setShowPopup(false);
    } catch {
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title: "Failed to save client.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    }
  };

  // Edit handler
  const handleEdit = (rowData: Client, index: number) => {
    setForm(rowData);
    setIsEditing(true);
    setEditingIndex(index);
    setShowPopup(true);
  };

  // Delete handler
  const handleDelete = async (rowData: Client, index: number) => {
    const client = clients[index];
    try {
      await clientService.delete(client.id!);
      const updated = clients.filter((_, i) => i !== index);
      setClients(updated);
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "success",
        title: "Client deleted successfully.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } catch (err: any) {
      if (err?.response?.status === 404) {
        // Remove from local state if not found
        const updated = clients.filter((_, i) => i !== index);
        setClients(updated);
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "info",
          title: "Client not found. Removed from list.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      } else {
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "error",
          title: "Failed to delete client.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
    }
  };

  // Table action column
  const actionBodyTemplate = (rowData: Client, options: any) => (
    <div className="d-flex gap-2">
      <Button
        icon="pi pi-pencil"
        rounded
        text
        severity="warning"
        tooltip="Edit"
        tooltipOptions={{ position: "top" }}
        onClick={() => handleEdit(rowData, options.rowIndex)}
      />
      {/* <Button
        icon="pi pi-trash"
        rounded
        text
        severity="danger"
        tooltip="Delete"
        tooltipOptions={{ position: "top" }}
        onClick={() => handleDelete(rowData, options.rowIndex)}
      /> */}
    </div>
  );

  return (
    <div className="content">
      <PageHeader title="Client Master" />
      <div className="content-body">
        <div className="container mt-4">
          <div className="row shadow">
            <div className="col-md-12 mb-3">
              <div className="d-flex align-items-center gap-2 my-3">
                <IconField iconPosition="left" className="flex-grow-1">
                  <InputIcon>
                    <Icon.Search />
                  </InputIcon>
                  <InputText
                    value={globalFilter}
                    onChange={(e) => {
                      setGlobalFilter(e.target.value);
                      setPage(1);
                      localStorage.setItem("clientMasterPage", "1");
                    }}
                    placeholder="Search"
                    style={{ minWidth: 250 }}
                  />
                </IconField>
                <button
                  type="button"
                  className="admin-modern-btn btn btn-primary"
                  onClick={() => {
                    setForm(initialClient);
                    setIsEditing(false);
                    setEditingIndex(null);
                    setShowPopup(true);
                  }}
                >
                  <Icon.PlusCircle /> Create Client
                </button>
              </div>

              <DataTable
                value={clients}
                dataKey="id"
                paginator
                first={(page - 1) * rows}
                rows={rows}
                totalRecords={totalRecords}
                onPage={(e) => {
                  const newPage = (e.page ?? 0) + 1;
                  setPage(newPage);
                  setRows(e.rows);
                  localStorage.setItem("clientMasterPage", newPage.toString());
                  localStorage.setItem("clientMasterRows", e.rows.toString());
                }}
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={(e) => {
                  setSortField(e.sortField);
                  setSortOrder(e.sortOrder === 1 ? 1 : -1);
                }}
                lazy
                loading={listLoading}
                rowsPerPageOptions={[5, 10, 20]}
                stripedRows
                sortMode="single"
                emptyMessage="No clients found."
                className="grid-table"
              >
                <Column field="name" header="Name" sortable />
                <Column field="description" header="Description" sortable />
                <Column field="address" header="Address" sortable />
                <Column field="zipCode" header="Zip Code" sortable />
                <Column
                  header="Actions"
                  body={actionBodyTemplate}
                  style={{ minWidth: "120px" }}
                />
              </DataTable>

              {/* Right Drawer for Create/Edit */}
              <Offcanvas
                show={showPopup}
                onHide={() => {
                  setShowPopup(false);
                  setForm(initialClient);
                  setErrors({});
                  setIsEditing(false);
                  setEditingIndex(null);
                }}
                placement="end"
                className="rmc-drawer"
              >
                <Offcanvas.Header closeButton>
                  <Offcanvas.Title>
                    {isEditing ? "Edit Client" : "Create Client"}
                  </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                  <div className="row g-2">
                    {/* Name (70%) and Code (30%) side by side */}
                    <div className="col-8 form-group">
                      <Form.Label>
                        Name <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        name="name"
                        placeholder="Client Name"
                        value={form.name || ""}
                        onChange={handleChange}
                        isInvalid={!!errors.name}
                        onKeyPress={(e) => {
                          if (!/[a-zA-Z\s]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                      {errors.name && (
                        <div className="text-danger tx-13">{errors.name}</div>
                      )}
                    </div>

                    <div className="col-4 form-group">
                      <Form.Label>Code</Form.Label>
                      <Form.Control
                        name="code"
                        placeholder="Code"
                        value={form.code || ""}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="col-md-12 form-group">
                      <Form.Label>
                        Description <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        name="description"
                        placeholder="Description"
                        value={form.description || ""}
                        onChange={handleChange}
                        isInvalid={!!errors.description}
                        rows={2}
                      />
                      {errors.description && (
                        <div className="text-danger tx-13">
                          {errors.description}
                        </div>
                      )}
                    </div>

                    <div className="col-md-12 form-group">
                      <Form.Label>
                        Address <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        name="address"
                        placeholder="Address"
                        value={form.address || ""}
                        onChange={handleChange}
                        isInvalid={!!errors.address}
                        rows={2}
                      />
                      {errors.address && (
                        <div className="text-danger tx-13">
                          {errors.address}
                        </div>
                      )}
                    </div>

                    {/* Country and State in a row */}
                    <div className="col-6 form-group">
                      <Form.Label>
                        Country <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        name="countryId"
                        value={form.countryId || 0}
                        onChange={handleChange}
                        isInvalid={!!errors.countryId}
                      >
                        <option value={0}>Select Country</option>
                        {countries.map((country) => (
                          <option key={country.id} value={country.id}>
                            {country.name}
                          </option>
                        ))}
                      </Form.Select>
                      {errors.countryId && (
                        <div className="text-danger tx-13">
                          {errors.countryId}
                        </div>
                      )}
                    </div>

                    <div className="col-6 form-group">
                      <Form.Label>
                        State <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        name="stateId"
                        value={form.stateId || 0}
                        onChange={handleChange}
                        isInvalid={!!errors.stateId}
                        disabled={!form.countryId}
                      >
                        <option value={0}>Select State</option>
                        {states.map((state) => (
                          <option key={state.id} value={state.id}>
                            {state.name}
                          </option>
                        ))}
                      </Form.Select>
                      {errors.stateId && (
                        <div className="text-danger tx-13">
                          {errors.stateId}
                        </div>
                      )}
                    </div>

                    {/* City and Zip Code in a row */}
                    <div className="col-6 form-group">
                      <Form.Label>
                        City <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        name="cityId"
                        value={form.cityId || 0}
                        onChange={handleChange}
                        isInvalid={!!errors.cityId}
                        disabled={!form.stateId}
                      >
                        <option value={0}>Select City</option>
                        {cities.map((city) => (
                          <option key={city.id} value={city.id}>
                            {city.name}
                          </option>
                        ))}
                      </Form.Select>
                      {errors.cityId && (
                        <div className="text-danger tx-13">{errors.cityId}</div>
                      )}
                    </div>

                    <div className="col-6 form-group">
                      <Form.Label>
                        Zip Code <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        name="zipCode"
                        placeholder="Zip Code"
                        value={form.zipCode || ""}
                        onChange={handleChange}
                        isInvalid={!!errors.zipCode}
                        inputMode="numeric"
                        maxLength={6}
                      />
                      {errors.zipCode && (
                        <div className="text-danger tx-13">
                          {errors.zipCode}
                        </div>
                      )}
                    </div>
                  </div>
                </Offcanvas.Body>

                <div className="rmc-drawer-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowPopup(false);
                      setForm(initialClient);
                      setErrors({});
                      setIsEditing(false);
                      setEditingIndex(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleAddOrUpdate}
                  >
                    {isEditing ? "Update" : "Add"}
                  </button>
                </div>
              </Offcanvas>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientMasterComponent;
export { ClientMasterComponent };
